import { MenuItem, Tooltip, styled } from "@mui/material";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { useMicrobiologyResultDataContext } from "Pages/MicroBiologyLis/ResultData/ResultDataContext";
import {
  MicrobiologyResultDataBulkValidate,
  MicrobiologyResultDataExportToExcelV2,
  MicrobiologyResultDataReportTest,
} from "Services/MicrobiologyResultData";
import RequisitionType from "Services/Requisition/RequisitionTypeService";
import { ExportAllRecords, ExportIcon, SelectedRecords } from "Shared/Icons";
import CustomPagination from "Shared/JsxPagination";
import useIsMobile from "Shared/hooks/useIsMobile";
import useLang from "Shared/hooks/useLanguage";
import usePagination from "Shared/hooks/usePagination";
import { AxiosError, AxiosResponse } from "axios";
import { saveAs } from "file-saver";
import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import ColumnSetup from "../../../Shared/ColumnSetup/ColumnSetup";
import PermissionComponent, {
  AnyPermission,
  getPermissionDisplayName,
} from "../../../Shared/Common/Permissions/PermissionComponent";
import ArrowBottomIcon from "../../../Shared/SVG/ArrowBottomIcon";
import BreadCrumbs from "../../../Utils/Common/Breadcrumb";
import { emptyObjectValues } from "../../../Utils/Common/Requisition";
import { AutocompleteStyle } from "../../../Utils/MuiStyles/AutocompleteStyles";
import {
  StyledDropButton,
  StyledDropMenu,
} from "../../../Utils/Style/Dropdownstyle";
import TransferTestsModal from "./Modal/TransferTestsModal";
import ViewContactsModal from "./Modal/ViewContactsModal";
import ViewHistoricalDataModal from "./Modal/ViewHistoricalDataModal";
import ReqDataGrid from "./ResultDataGrid";

const TabSelected = styled(Tab)(AutocompleteStyle());

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && (
      <Box sx={{ p: 0 }}>
        <Typography>{children}</Typography>
      </Box>
    )}
  </div>
);

const getA11yProps = (index: number) => ({
  id: `simple-tab-${index}`,
  "aria-controls": `simple-tabpanel-${index}`,
});

const MicrobiologyResultData: React.FC = () => {
  const {
    data,
    filterData,
    setFilterData,
    searchValue,
    setSearchValue,
    selectedBox,
    setSelectedBox,
    loadGridData,
    apiCalls,
    loading,
    setCheckedAll,
    total,
    value,
    setValue,
    setRowsToExpand,
    setIsMasterExpandTriggered,
  } = useMicrobiologyResultDataContext();

  const { t } = useLang();
  // const isMobile = useIsMobile();
  const [filters, setFilters] = useState<any[]>([]);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(false);
  const [searchedTags, setSearchedTags] = useState<string[]>([]);
  const [isInitialRender2, setIsInitialRender2] = useState(false);
  const [triggerSearchData, setTriggerSearchData] = useState(false);
  // const [anchorEl, setAnchorEl] = useState({
  //   dropdown1: null,
  //   dropdown2: null,
  //   dropdown3: null,
  //   dropdown4: null,
  // } as Record<string, any>);
  // const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showViewContacts, setShowViewContacts] = useState(false);
  const [showViewHistoricalData, setShowViewHistoricalData] = useState(false);
  const [historicalDataReqOrderId, setHistoricalDataReqOrderId] = useState<number | undefined>();
  const [showTransferTests, setShowTransferTests] = useState(false);

  const {
    curPage,
    nextPage,
    pageNumbers,
    pageSize,
    prevPage,
    setCurPage,
    setPageSize,
    setTotal,
    showPage,
    totalPages,
  } = usePagination();

  // const openDrop = Object.values(anchorEl).some(Boolean);

  // const getSelectedRequisitionOrderIds = () =>
  //   (selectedBox?.requisitionId || [])
  //     .map((item: any) => item?.requisitionOrderId)
  //     .filter((id: any) => id !== null && id !== undefined);

  const clearSelectedBox = () => {
    setSelectedBox((prevState: any) => ({
      ...prevState,
      requisitionId: [],
    }));
    setCheckedAll(false);
  };

  const resetFilterData = () => {
    filterData.filters = [];
    filterData.sortColumn = "";
    filterData.pageSize = 50;
    filterData.pageNumber = 1;
    filterData.sortDirection = "";
  };

  useEffect(() => {
    setTotal(total);
  }, [total, setTotal]);

  useEffect(() => {
    setRowsToExpand([]);
    setIsMasterExpandTriggered(false);
  }, [curPage, pageSize, setRowsToExpand, setIsMasterExpandTriggered]);

  useEffect(() => {
    setFilterData((prev) => ({
      ...prev,
      pageNumber: curPage,
      pageSize: pageSize,
    }));
  }, [curPage, pageSize, setFilterData]);

  useEffect(() => {
    if (!isInitialRender2) {
      setIsInitialRender2(true);
      return;
    }
    loadGridData(true);
  }, [pageSize, curPage, triggerSearchData]);

  useEffect(() => {
    const filteredObject: Record<string, any> = {};
    filters?.forEach((filter: any) => {
      filteredObject[filter.label] = filter.filterValue;
    });

    const uniqueKeys = new Set<string>();
    Object.entries(filteredObject).forEach(([key, value]) => {
      if (value) {
        uniqueKeys.add(key);
      }
    });
    setSearchedTags(Array.from(uniqueKeys));
  }, [filters]);

  useEffect(() => {
    if (isInitialRender) {
      if (searchedTags.length === 0) {
        resetSearch();
      }
    } else {
      setIsInitialRender(true);
    }
  }, [searchedTags.length, isInitialRender]);

  useEffect(() => {
    filterData.tabId = 1;
    apiCalls();
  }, []);

  // const handleClick = (event: React.MouseEvent, dropdownName: string) => {
  //   setAnchorEl((prev) => ({ ...prev, [dropdownName]: event.currentTarget }));
  // };

  // const handleClose = (dropdownName: string) => {
  //   setAnchorEl((prev) => ({ ...prev, [dropdownName]: null }));
  // };

  const handleChange = async (event: React.SyntheticEvent, newValue: number) => {
    const tabIdMatch = (event.currentTarget as HTMLElement).id.match(/\d+/);
    const tabId = tabIdMatch ? parseInt(tabIdMatch[0]) : null;

    setValue(newValue);
    if (tabId && filterData?.tabId !== tabId) {
      const emptySearchObj = emptyObjectValues(searchValue);
      setSearchValue(emptySearchObj);
      filterData.tabId = tabId;
      setCurPage(1);
      setRowsToExpand([]);
      filterData.filters = [];
      filterData.pageNumber = 1;
      setFilters([]);
      await loadGridData();
    }
    clearSelectedBox();
  };

  // const checkForOnHoldRecords = (selectedIds: number[]) => {
  //   const onHoldRecords = (selectedBox?.requisitionId || []).filter((record: any) => {
  //     const foundRecord = (data?.gridData || []).find(
  //       (item: any) => item?.RequisitionOrderId === record?.requisitionOrderId
  //     );
  //     return foundRecord?.RequisitionStatus === "On Hold";
  //   });
  //   return onHoldRecords.length > 0;
  // };

  // const checkForInvalidStatus = (selectedIds: number[], requiredStatus: string) => {
  //   const invalidRecords = (selectedBox?.requisitionId || []).filter((record: any) => {
  //     const foundRecord = (data?.gridData || []).find(
  //       (item: any) => item?.RequisitionOrderId === record?.requisitionOrderId
  //     );
  //     return foundRecord?.LisStatus !== requiredStatus;
  //   });
  //   return invalidRecords.length > 0;
  // };

  // const handleTestReport = () => {
  //   const selectedIds = getSelectedRequisitionOrderIds();
  //   if (selectedIds.length === 0) {
  //     toast.error(t("Please select at least one record"));
  //     return;
  //   }

  //   if (checkForOnHoldRecords(selectedIds)) {
  //     toast.error(
  //       t("Some requisitions are still on hold. Please complete all tests before proceeding.")
  //     );
  //     return;
  //   }

  //   MicrobiologyResultDataReportTest(selectedIds)
  //     .then((res: AxiosResponse) => {
  //       if (res?.data?.statusCode === 200) {
  //         toast.success(t(res?.data?.message || "Report generated successfully"));
  //         loadGridData();
  //         clearSelectedBox();
  //       } else {
  //         toast.error(t(res?.data?.message || "Failed to generate report"));
  //       }
  //     })
  //     .catch((err: any) => {
  //       console.error("Error generating report:", err);
  //       toast.error(t("An error occurred while generating the report"));
  //     });
  // };

  // const handleBulkValidate = () => {
  //   const selectedIds = getSelectedRequisitionOrderIds();
  //   if (selectedIds.length === 0) {
  //     toast.error(t("Please select at least one record"));
  //     return;
  //   }

  //   if (checkForInvalidStatus(selectedIds, "Ready to Validate")) {
  //     toast.error(t("Please select records with the status 'Ready to Validate'."));
  //     return;
  //   }

  //   MicrobiologyResultDataBulkValidate({ requisitionOrderIds: selectedIds })
  //     .then((res: AxiosResponse) => {
  //       if (res?.data?.statusCode === 200) {
  //         toast.success(res?.data?.message || t("Validation successful"));
  //         loadGridData();
  //         clearSelectedBox();
  //       } else {
  //         toast.error(res?.data?.message || t("Validation failed"));
  //       }
  //     })
  //     .catch((err: any) => {
  //       console.error("Error validating data:", err);
  //       toast.error(t("An error occurred while validating the data"));
  //     });
  // };

  // const base64ToExcel = (base64: string, filename: string) => {
  //   const decodedBase64 = atob(base64);
  //   const workbook = XLSX.read(decodedBase64, { type: "binary" });
  //   const excelBuffer = XLSX.write(workbook, {
  //     bookType: "xlsx",
  //     type: "array",
  //   });
  //   const excelBlob = new Blob([excelBuffer], {
  //     type: "application/octet-stream",
  //   });
  //   saveAs(excelBlob, `${filename}.xlsx`);
  // };

  // const handleExport = (selectedOnly = false) => {
  //   const selectedIds = getSelectedRequisitionOrderIds();
  //   if (selectedOnly && selectedIds.length === 0) {
  //     toast.error(t("Please Select Minimum 1 Record"));
  //     return;
  //   }

  //   const exportParams = {
  //     tabId: filterData.tabId,
  //     sortColumn: filterData.sortColumn,
  //     sortDirection: filterData.sortDirection,
  //     filters: filterData.filters,
  //     ...(selectedOnly && {
  //       selectedRow: (selectedBox?.requisitionId || []).map((item: any) =>
  //         filterData.tabId === 4 ? item?.id : item?.requisitionOrderId
  //       ).filter((id: any) => id !== null && id !== undefined),
  //     }),
  //   };

  //   MicrobiologyResultDataExportToExcelV2(exportParams)
  //     .then((res: AxiosResponse) => {
  //       if (res?.data?.statusCode === 200 && res?.data?.data?.fileContents) {
  //         toast.success(t(res?.data?.message || "Export successful"));
  //         base64ToExcel(res.data.data.fileContents, "Microbiology Result Data");
  //         clearSelectedBox();
  //       } else {
  //         toast.error(t(res?.data?.message));
  //       }
  //     })
  //     .catch((err: any) => {
  //       console.error("Error exporting data:", err);
  //       toast.error(t("An error occurred while exporting data"));
  //     });
  // };

  const handleSearch = () => {
    setCurPage(1);
    setTriggerSearchData((prev) => !prev);
  };

  const resetSearch = () => {
    clearSelectedBox();
    resetFilterData();
    setFilters([]);
    const emptySearchObj = emptyObjectValues(searchValue);
    setSearchValue(emptySearchObj);
    loadGridData(true);
  };

  const handleTagRemoval = (clickedTag: string) => {
    const filteredTags = filters.filter((tab: any) => tab.label !== clickedTag);
    const filteredDataTags = filterData.filters.filter((tab: any) => tab.label !== clickedTag);

    setFilters(filteredTags);
    setSearchValue((prevValue: any) => ({
      ...prevValue,
      filters: filteredTags,
    }));
    setFilterData((prevValue: any) => ({
      ...prevValue,
      filters: filteredDataTags,
    }));
  };

  // const handleViewHistoricalData = () => {
  //   const selectedIds = getSelectedRequisitionOrderIds();
  //   if (selectedIds.length === 0) {
  //     toast.error(t("Please select at least one record"));
  //     return;
  //   }
  //   if (selectedIds.length > 1) {
  //     toast.error(t("Please select only one record to view historical data"));
  //     return;
  //   }
  //   setHistoricalDataReqOrderId(selectedIds[0]);
  //   setShowViewHistoricalData(true);
  // };

  return (
    <>
      <ColumnSetup
        show={showSetupModal}
        closeSetupModal={() => setShowSetupModal(false)}
        loadData={apiCalls}
        value={filterData.tabId}
        columnsToUse={data?.gridHeaders?.[value]?.tabHeaders || []}
      />

      <div className="d-flex flex-column flex-column-fluid">
        <div className="app-toolbar py-3 py-lg-3">
          <div className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center">
            <BreadCrumbs />
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <Tooltip title={t("Setup")} arrow placement="top">
                <button
                  className="btn btn-icon btn-sm fw-bold btn-setting btn-icon-light"
                  onClick={() => setShowSetupModal(true)}
                >
                  <i className="fa fa-gear"></i>
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="d-flex flex-column flex-column-fluid">
          <div className="app-content flex-column-fluid">
            <div className="app-container container-fluid">
              <Tabs
                value={value}
                onChange={handleChange}
                TabIndicatorProps={{ style: { background: "transparent" } }}
                className="min-h-auto"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  "& .MuiTabs-scrollButtons": {
                    width: 0,
                    transition: "width 0.7s ease",
                    "&:not(.Mui-disabled)": {
                      width: "48px",
                    },
                  },
                }}
              >
                {Array.isArray(data.gridHeaders) &&
                  data?.gridHeaders?.map((items: any) => (
                    <TabSelected
                      data-test-id={items?.tabName?.replace(/\s/g, "")}
                      key={items.tabID}
                      label={t(items.tabName)}
                      {...getA11yProps(items.tabID)}
                      className="fw-bold text-capitalize"
                      disabled={loading}
                    />
                  ))}
              </Tabs>
              <div className="card tab-content-card">
                <div className="card-body py-2">
                  <div className="d-flex gap-4 flex-wrap mb-1">
                    {searchedTags.map((tag) => (
                      <div
                        className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
                        onClick={() => handleTagRemoval(tag)}
                        key={tag}
                      >
                        <span className="fw-bold">{tag}</span>
                        <i className="bi bi-x"></i>
                      </div>
                    ))}
                  </div>
                  <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mb-2 col-12 responsive-flexed-actions">
                    <div className="d-flex gap-2 responsive-flexed-actions">
                      <div className="d-flex align-items-center">
                        <span className="fw-400 mr-3">{t("Records")}</span>
                        <select
                          id={`MicrobiologyResultDataRecords`}
                          className="form-select w-125px h-33px rounded py-2"
                          onChange={async (e) => {
                            const newPageSize = parseInt(e.target.value);
                            setPageSize(newPageSize);
                            filterData.pageSize = newPageSize;
                            await loadGridData();
                          }}
                          value={filterData.pageSize}
                        >
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                          <option value="150">150</option>
                          <option value="200">200</option>
                        </select>
                      </div>

                      {/* <div className="d-flex justify-content-center gap-2">
                        <AnyPermission
                          moduleName="Microbiology LIS"
                          pageName="Result Data"
                          permissionIdentifiers={[
                            "ExportAllRecord",
                            "ExportSelectedRecords",
                          ]}
                        >
                          <StyledDropButton
                            id={`MicrobiologyResultDataBulkExport`}
                            aria-controls={
                              openDrop ? "demo-positioned-menu2" : undefined
                            }
                            aria-haspopup="true"
                            aria-expanded={openDrop ? "true" : undefined}
                            onClick={(event) => handleClick(event, "dropdown2")}
                            className="btn btn-excle btn-sm"
                          >
                            <ExportIcon />
                            <span className="svg-icon svg-icon-5 m-0">
                              <ArrowBottomIcon />
                            </span>
                          </StyledDropButton>
                          <StyledDropMenu
                            id={`MicrobiologyResultDataBulkExportMenu`}
                            aria-labelledby="demo-positioned-button2"
                            anchorEl={anchorEl.dropdown2}
                            open={Boolean(anchorEl.dropdown2)}
                            onClose={() => handleClose("dropdown2")}
                            anchorOrigin={{
                              vertical: "top",
                              horizontal: "left",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "left",
                            }}
                          >
                            <PermissionComponent
                              moduleName="Microbiology LIS"
                              pageName="Result Data"
                              permissionIdentifier="ExportAllRecord"
                            >
                              <MenuItem className="p-0">
                                <a
                                  id={`MicrobiologyResultDataExportAll`}
                                  className="p-0 w-200px text-dark"
                                  onClick={() => {
                                    handleClose("dropdown2");
                                    handleExport(false);
                                  }}
                                >
                                  <ExportAllRecords />
                                  {t("Export All Records")}
                                </a>
                              </MenuItem>
                            </PermissionComponent>
                            <PermissionComponent
                              moduleName="Microbiology LIS"
                              pageName="Result Data"
                              permissionIdentifier="ExportSelectedRecords"
                            >
                              <MenuItem className="p-0">
                                <a
                                  id={`MicrobiologyResultDataExportSelected`}
                                  className="p-0 w-200px text-dark"
                                  onClick={() => {
                                    handleClose("dropdown2");
                                    handleExport(true);
                                  }}
                                >
                                  <SelectedRecords />
                                  {t("Export Selected Records")}
                                </a>
                              </MenuItem>
                            </PermissionComponent>
                          </StyledDropMenu>
                        </AnyPermission>
                      </div> */}
                    </div>

                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                      {/* {filterData.tabId === 1 && isMobile && (
                        <StyledDropButton
                          id={`BloodResultDataBulkAction`}
                          aria-controls={
                            openDrop ? "demo-positioned-menu1" : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={openDrop ? "true" : undefined}
                          onClick={(event) => handleClick(event, "dropdown1")}
                          className="btn btn-info btn-sm"
                        >
                          {t("Bulk Action")}
                          <span className="svg-icon svg-icon-5 m-0">
                            <ArrowBottomIcon />
                          </span>
                        </StyledDropButton>
                      )} */}
                      {/* <StyledDropMenu
                        id={`BloodResultDataBulkActionMenu`}
                        aria-labelledby="demo-positioned-button1"
                        anchorEl={anchorEl.dropdown1}
                        open={Boolean(anchorEl.dropdown1)}
                        onClose={() => handleClose("dropdown1")}
                        sx={{ padding: 0 }}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                      >
                        <PermissionComponent
                          moduleName="Microbiology LIS"
                          pageName="Result Data"
                          permissionIdentifier="BulkReport"
                        >
                          <MenuItem className="p-0">
                            <a
                              id={`BloodResultDataBulkReport`}
                              className="w-100 text-dark"
                              onClick={() => {
                                handleClose("dropdown1");
                                handleTestReport();
                              }}
                            >
                              {t("Report")}
                            </a>
                          </MenuItem>
                        </PermissionComponent>
                        <PermissionComponent
                          moduleName="Microbiology LIS"
                          pageName="Result Data"
                          permissionIdentifier="BulkValidate"
                        >
                          <MenuItem className="p-0">
                            <a
                              id={`BloodResultDataBulkValidate`}
                              className="w-100 text-dark"
                              onClick={() => {
                                handleClose("dropdown1");
                                handleBulkValidate();
                              }}
                            >
                              {t("Validate")}
                            </a>
                          </MenuItem>
                        </PermissionComponent>
                      </StyledDropMenu> */}

                      <button
                        id={`MicrobiologyResultDataSearch`}
                        onClick={handleSearch}
                        className="btn btn-linkedin btn-sm fw-500"
                        aria-controls="Search"
                      >
                        {t("Search")}
                      </button>
                      <button
                        onClick={resetSearch}
                        type="button"
                        className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
                        id={`MicrobiologyResultDataReset`}
                      >
                        <span>
                          <span>{t("Reset")}</span>
                        </span>
                      </button>
                    </div>
                  </div>
                  <TabPanel value={value} index={value}>
                    <ReqDataGrid
                      tabsInfo={data?.gridHeaders?.[value]?.tabHeaders || []}
                      value={value}
                      filters={filters}
                      setFilters={setFilters}
                      setCurPage={setCurPage}
                    />
                  </TabPanel>
                  {!loading && (
                    <CustomPagination
                      curPage={curPage}
                      nextPage={nextPage}
                      pageNumbers={pageNumbers}
                      pageSize={pageSize}
                      prevPage={prevPage}
                      showPage={showPage}
                      total={total}
                      totalPages={totalPages}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <ViewContactsModal
          show={showViewContacts}
          onHide={() => setShowViewContacts(false)}
        />

        <ViewHistoricalDataModal
          show={showViewHistoricalData}
          onHide={() => {
            setShowViewHistoricalData(false);
            setHistoricalDataReqOrderId(undefined);
          }}
          reqOrderId={historicalDataReqOrderId}
          tabId={filterData?.tabId}
        />

        <TransferTestsModal
          show={showTransferTests}
          onHide={() => setShowTransferTests(false)}
        />
      </div>
    </>
  );
};

export default React.memo(MicrobiologyResultData);
